import { Services } from "./Services";
import { HttpAPIService } from "@/core/api/HttpAPIService";
import { SentryLoggerService } from "@/core/Logger/SentryLoggerService";
import { DefaultQueryBuilder } from "@/core/api/DefaultQueryBuilder";

/**
 * Creates and configures all application services.
 *
 * This factory function is called once at application startup to instantiate
 * all services that will be provided through the ServicesContext.
 *
 * @param baseUrl - Base URL for API requests (defaults to env var VITE_API_BASE_URL or '/api/v1')
 * @param getToken - Optional function to retrieve current auth token. If not provided,
 *                   token injection via AuthInterceptor global fetch override will be used instead.
 * @returns Services object with all configured services
 *
 * @example
 * ```tsx
 * // Usage at application startup
 * const services = createServices();
 * root.render(
 *   <ServicesProvider services={services}>
 *     <App />
 *   </ServicesProvider>
 * );
 * ```
 */
export function createServices(
  baseUrl?: string,
  getToken?: () => string | null,
): Services {
  const logger = new SentryLoggerService();

  const api = new HttpAPIService(
    baseUrl || import.meta.env.VITE_API_BASE_URL || "/api/v1",
    logger,
    getToken,
  );

  const queryBuilder = new DefaultQueryBuilder();

  return {
    api,
    logger,
    queryBuilder,
    // Domain services will be added here as modules migrate
    // Example:
    // user: new UserService(api, queryBuilder),
    // projects: new ProjectService(api, queryBuilder),
  };
}
